// Tiny stroke icon set. SVG paths keyed by name.
// Source: designproject/files/tokens.jsx.

import type { JSX } from 'preact';

type StrokeProps = {
  fill: 'none';
  stroke: string;
  strokeWidth: number;
  strokeLinecap: 'round';
  strokeLinejoin: 'round';
};

/**
 * Builds the icon path registry. Captured inside a function so that the
 * `color` and `strokeWidth` props parametrise the generated SVG children.
 */
function buildPaths(
  color: string,
  strokeWidth: number,
): Record<string, JSX.Element> {
  const p: StrokeProps = {
    fill: 'none',
    stroke: color,
    strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  return {
    mic: (
      <>
        <rect x="9" y="3" width="6" height="11" rx="3" {...p} />
        <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3" {...p} />
      </>
    ),
    keyboard: (
      <>
        <rect x="3" y="6" width="18" height="13" rx="2.5" {...p} />
        <path
          d="M7 10h.01M11 10h.01M15 10h.01M17 10h.01M7 13.5h.01M11 13.5h.01M15 13.5h.01M17 13.5h.01M8 16.5h8"
          {...p}
        />
      </>
    ),
    camera: (
      <>
        <path
          d="M3 8.5A2.5 2.5 0 0 1 5.5 6h1.7L8.5 4h7l1.3 2h1.7A2.5 2.5 0 0 1 21 8.5v9A2.5 2.5 0 0 1 18.5 20h-13A2.5 2.5 0 0 1 3 17.5z"
          {...p}
        />
        <circle cx="12" cy="13" r="3.5" {...p} />
      </>
    ),
    home: (
      <>
        <path
          d="M4 11.5 12 4l8 7.5V20a1 1 0 0 1-1 1h-4v-6h-6v6H5a1 1 0 0 1-1-1z"
          {...p}
        />
      </>
    ),
    tree: (
      <>
        <path
          d="M5 4h6v6H5zM13 4h6v4h-6zM13 12h6v4h-6zM13 18h6v2h-6zM11 7h2M8 10v10h3M11 18h2"
          {...p}
        />
      </>
    ),
    cal: (
      <>
        <rect x="3.5" y="5" width="17" height="15" rx="2.5" {...p} />
        <path d="M3.5 9.5h17M8 3v4M16 3v4" {...p} />
      </>
    ),
    bell: (
      <>
        <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15zM10 21a2 2 0 0 0 4 0" {...p} />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="6.5" {...p} />
        <path d="m20 20-3.5-3.5" {...p} />
      </>
    ),
    plus: (
      <>
        <path d="M12 5v14M5 12h14" {...p} />
      </>
    ),
    close: (
      <>
        <path d="M6 6l12 12M18 6L6 18" {...p} />
      </>
    ),
    check: (
      <>
        <path d="M5 12.5 10 17 19 7.5" {...p} />
      </>
    ),
    chevR: (
      <>
        <path d="M9 6l6 6-6 6" {...p} />
      </>
    ),
    chevD: (
      <>
        <path d="M6 9l6 6 6-6" {...p} />
      </>
    ),
    chevL: (
      <>
        <path d="M15 6l-6 6 6 6" {...p} />
      </>
    ),
    back: (
      <>
        <path d="M19 12H5M11 6l-6 6 6 6" {...p} />
      </>
    ),
    more: (
      <>
        <circle cx="5" cy="12" r="1.3" fill={color} stroke="none" />
        <circle cx="12" cy="12" r="1.3" fill={color} stroke="none" />
        <circle cx="19" cy="12" r="1.3" fill={color} stroke="none" />
      </>
    ),
    undo: (
      <>
        <path d="M9 13a6 6 0 1 0 6-6H3M3 7v6h6" {...p} />
      </>
    ),
    move: (
      <>
        <path
          d="M12 3v18M3 12h18M9 6l3-3 3 3M9 18l3 3 3-3M6 9l-3 3 3 3M18 9l3 3-3 3"
          {...p}
        />
      </>
    ),
    trash: (
      <>
        <path
          d="M5 7h14M10 11v6M14 11v6M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13M9 7V4h6v3"
          {...p}
        />
      </>
    ),
    edit: (
      <>
        <path d="M4 20h4l11-11-4-4L4 16zM14 6l4 4" {...p} />
      </>
    ),
    boiler: (
      <>
        <rect x="6" y="3" width="12" height="18" rx="2" {...p} />
        <circle cx="12" cy="10" r="2.5" {...p} />
        <path d="M9 16h6M10 19h4" {...p} />
      </>
    ),
    filter: (
      <>
        <path d="M5 5h14l-5 7v6l-4 2v-8z" {...p} />
      </>
    ),
    drop: (
      <>
        <path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11z" {...p} />
      </>
    ),
    spark: (
      <>
        <path
          d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6zM18.5 14l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z"
          fill={color}
          stroke="none"
        />
      </>
    ),
    photo: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" {...p} />
        <path d="m3 17 5-5 4 4 3-3 6 6" {...p} />
        <circle cx="9" cy="10" r="1.3" {...p} />
      </>
    ),
    box: (
      <>
        <path
          d="M3 7.5 12 3l9 4.5M3 7.5v9L12 21l9-4.5v-9M3 7.5 12 12m9-4.5L12 12m0 0v9"
          {...p}
        />
      </>
    ),
    pkg: (
      <>
        <rect x="4" y="5" width="16" height="15" rx="1.5" {...p} />
        <path d="M9 5v3h6V5" {...p} />
      </>
    ),
    map: (
      <>
        <path d="M9 4 4 6v14l5-2 6 2 5-2V4l-5 2zM9 4v14M15 6v14" {...p} />
      </>
    ),
    play: (
      <>
        <path d="M7 5v14l12-7z" {...p} />
      </>
    ),
    flame: (
      <>
        <path d="M12 3c1 5 5 5 5 10a5 5 0 0 1-10 0c0-3 2-3 2-6 2 1 3 1 3-4z" {...p} />
      </>
    ),
    bolt: (
      <>
        <path d="M13 3 5 14h5l-1 7 8-11h-5z" {...p} />
      </>
    ),
    shield: (
      <>
        <path d="M12 3 4 6v6c0 5 4 8 8 9 4-1 8-4 8-9V6z" {...p} />
      </>
    ),
    sun: (
      <>
        <circle cx="12" cy="12" r="4" {...p} />
        <path
          d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4 7 17M17 7l1.4-1.4"
          {...p}
        />
      </>
    ),
    snow: (
      <>
        <path
          d="M12 3v18M3 12h18M5 5l14 14M19 5 5 19M9 3l3 2 3-2M9 21l3-2 3 2"
          {...p}
        />
      </>
    ),
    user: (
      <>
        <circle cx="12" cy="8" r="4" {...p} />
        <path d="M4 21c0-4 4-7 8-7s8 3 8 7" {...p} />
      </>
    ),
    waveform: (
      <>
        <path d="M3 12h2M7 9v6M11 6v12M15 8v8M19 10v4M21 12h0" {...p} />
      </>
    ),
    list: (
      <>
        <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" {...p} />
      </>
    ),
    pin: (
      <>
        <path d="M12 21s-7-7-7-12a7 7 0 0 1 14 0c0 5-7 12-7 12z" {...p} />
        <circle cx="12" cy="9" r="2.5" {...p} />
      </>
    ),
    leaf: (
      <>
        <path d="M5 19c0-8 5-14 14-14-1 9-6 14-14 14zM5 19l8-8" {...p} />
      </>
    ),
    stop: (
      <>
        <rect x="6" y="6" width="12" height="12" rx="2" {...p} />
      </>
    ),
    send: (
      <>
        <path d="M5 12 20 5l-5 15-3-7z" {...p} />
      </>
    ),
    sliders: (
      <>
        <path
          d="M4 7h10M18 7h2M4 12h2M10 12h10M4 17h12M20 17h0M16 4v6M8 9v6M14 14v6"
          {...p}
        />
      </>
    ),
    info: (
      <>
        <circle cx="12" cy="12" r="9" {...p} />
        <path d="M12 8h.01M11 12h1v5h1" {...p} />
      </>
    ),
    history: (
      <>
        <path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 4v4h4M12 8v4l3 2" {...p} />
      </>
    ),
    folder: (
      <>
        <path
          d="M3 6.5A1.5 1.5 0 0 1 4.5 5h4l2 2h9A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5z"
          {...p}
        />
      </>
    ),
    item: (
      <>
        <circle cx="12" cy="12" r="3" {...p} />
      </>
    ),

    // --- Rooms / spaces ---
    kitchen: (
      <>
        <rect x="4" y="3" width="16" height="18" rx="2" {...p} />
        <path d="M4 13h16M8 6v4M11 6v4M8 17h.01M14 16h3" {...p} />
        <circle cx="8" cy="8" r="2.5" {...p} />
      </>
    ),
    bedroom: (
      <>
        <path d="M3 19v-7h13a4 4 0 0 1 4 4v3M3 19v-3M3 16h17M3 12V7" {...p} />
        <circle cx="8" cy="9.5" r="2" {...p} />
      </>
    ),
    bathroom: (
      <>
        <path d="M4 12V6a2 2 0 0 1 4 0M4 12h16v2a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4zM7 18l-1 3M17 18l1 3" {...p} />
        <path d="M6 6h.01" {...p} />
      </>
    ),
    livingRoom: (
      <>
        <path d="M5 13v-2a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v2M3 13h18v5h-2v-2H5v2H3z" {...p} />
      </>
    ),
    hallway: (
      <>
        <path d="M5 21V4l8-1v18M5 21h12V8h-4M9 12h.01" {...p} />
      </>
    ),
    pantry: (
      <>
        <rect x="5" y="3" width="14" height="18" rx="1.5" {...p} />
        <path d="M5 9h14M5 15h14M11 5.5v1.5M11 11v1.5M11 17v1.5" {...p} />
      </>
    ),
    garageRoom: (
      <>
        <path d="M3 20V9l9-5 9 5v11M3 20h18M6 20v-7h12v7" {...p} />
        <path d="M6 16h12" {...p} />
      </>
    ),
    balcony: (
      <>
        <path d="M5 3v8h14V3M5 11v10M9 11v10M13 11v10M17 11v10M3 21h18M3 15h18" {...p} />
      </>
    ),
    kidsRoom: (
      <>
        <path d="M5 21v-6a7 7 0 0 1 14 0v6M5 18h14" {...p} />
        <circle cx="12" cy="6" r="2.5" {...p} />
      </>
    ),
    study: (
      <>
        <path d="M4 19V5a1 1 0 0 1 1-1h13a1 1 0 0 1 1 1v14M4 19h15M8 8h7M8 11h7M8 14h4" {...p} />
      </>
    ),
    attic: (
      <>
        <path d="M3 14 12 4l9 10M6 14v6h12v-6M10 20v-4h4v4" {...p} />
      </>
    ),
    basement: (
      <>
        <path d="M3 10 12 4l9 6v10H3zM7 20v-6h4v6M14 14h4v3h-4z" {...p} />
      </>
    ),
    closet: (
      <>
        <rect x="5" y="3" width="14" height="18" rx="1.5" {...p} />
        <path d="M12 3v18M10 11h.01M14 11h.01" {...p} />
      </>
    ),
    laundry: (
      <>
        <rect x="4" y="3" width="16" height="18" rx="2" {...p} />
        <circle cx="12" cy="13" r="5" {...p} />
        <path d="M9 12a3 3 0 0 1 6 1M7 6h.01M10 6h.01" {...p} />
      </>
    ),
    garden: (
      <>
        <path d="M12 21V11M12 11c0-3 2-5 5-5 0 3-2 5-5 5zM12 14c0-3-2-5-5-5 0 3 2 5 5 5zM5 21h14" {...p} />
      </>
    ),
    toilet: (
      <>
        <path d="M6 4v7a5 5 0 0 0 10 0V4M16 6h3v3a3 3 0 0 1-3 3M11 16v5M8 21h6" {...p} />
      </>
    ),

    // --- Appliances ---
    fridge: (
      <>
        <rect x="6" y="3" width="12" height="18" rx="2" {...p} />
        <path d="M6 10h12M9 6v2M9 13v3" {...p} />
      </>
    ),
    washer: (
      <>
        <rect x="5" y="3" width="14" height="18" rx="2" {...p} />
        <circle cx="12" cy="14" r="5" {...p} />
        <circle cx="12" cy="14" r="1.6" {...p} />
        <path d="M8 6h.01M11 6h.01" {...p} />
      </>
    ),
    stove: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" {...p} />
        <circle cx="8.5" cy="8.5" r="2.5" {...p} />
        <circle cx="15.5" cy="8.5" r="2.5" {...p} />
        <circle cx="8.5" cy="15.5" r="2.5" {...p} />
        <circle cx="15.5" cy="15.5" r="2.5" {...p} />
      </>
    ),
    oven: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" {...p} />
        <path d="M3 8h18M7 5.5h.01M11 5.5h.01M15 5.5h.01" {...p} />
        <rect x="6" y="11" width="12" height="7" rx="1" {...p} />
      </>
    ),
    microwave: (
      <>
        <rect x="2" y="5" width="20" height="14" rx="2" {...p} />
        <rect x="5" y="8" width="10" height="8" rx="1" {...p} />
        <path d="M18 9v6" {...p} />
        <circle cx="18" cy="9" r="0.6" fill={color} stroke="none" />
      </>
    ),
    dishwasher: (
      <>
        <rect x="5" y="3" width="14" height="18" rx="2" {...p} />
        <path d="M5 8h14M8 5.5h6M9 12v6M12 12v6M15 12v6" {...p} />
      </>
    ),
    tv: (
      <>
        <rect x="3" y="4" width="18" height="12" rx="2" {...p} />
        <path d="M8 20h8M12 16v4" {...p} />
      </>
    ),
    ac: (
      <>
        <rect x="3" y="5" width="18" height="8" rx="2" {...p} />
        <path d="M3 9.5h18M7 17c0-1.5 1-2 1-3.5M12 18c0-1.5 1-2 1-3.5M17 17c0-1.5 1-2 1-3.5" {...p} />
      </>
    ),
    vacuum: (
      <>
        <circle cx="11" cy="15" r="6" {...p} />
        <circle cx="11" cy="15" r="2" {...p} />
        <path d="M16 11 19 6a2 2 0 0 1 2-1M5 15h2" {...p} />
      </>
    ),
    kettle: (
      <>
        <path d="M5 9h11l2 4v6a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1zM16 11h3M5 9 7 5h6M9 5V3" {...p} />
      </>
    ),
    hood: (
      <>
        <path d="M3 5h18l-2 6H5zM7 11v3M12 11v3M17 11v3M10 16h4v3h-4z" {...p} />
      </>
    ),
    radiator: (
      <>
        <rect x="3" y="6" width="18" height="12" rx="2" {...p} />
        <path d="M7 6v12M11 6v12M15 6v12M5 18v3M19 18v3" {...p} />
      </>
    ),
    fan: (
      <>
        <circle cx="12" cy="12" r="2" {...p} />
        <path d="M12 10c-1-3-3-5-6-4 0 3 2 5 6 4zM12 14c1 3 3 5 6 4 0-3-2-5-6-4zM14 12c3-1 5-3 4-6-3 0-5 2-4 6zM10 12c-3 1-5 3-4 6 3 0 5-2 4-6z" {...p} />
      </>
    ),

    // --- Tools ---
    drill: (
      <>
        <path d="M3 8h10v6H7l-1 3H4v-3a2 2 0 0 1-1-2zM13 9h3l4-2v6l-4-2h-3M9 14v4" {...p} />
      </>
    ),
    hammer: (
      <>
        <path d="M11 7 7 3 3 7l4 4M9 9l3-3 9 4-2 4zM12 12 6 18l-2 3" {...p} />
      </>
    ),
    screwdriver: (
      <>
        <path d="M14 3h5v5l-4 4-3-3zM12 9 4 17l-1 4 4-1 8-8" {...p} />
      </>
    ),
    wrench: (
      <>
        <path d="M15 3a6 6 0 0 0-7.5 7.5L3 15l6 6 4.5-4.5A6 6 0 0 0 21 9l-3.5 3.5-3-3z" {...p} />
      </>
    ),
    saw: (
      <>
        <path d="M3 8h13l5 5-3 3-2-2M3 8l2 3 2-3 2 3 2-3 2 3 2-3M5 16l2 4h6l-1-3" {...p} />
      </>
    ),
    ladder: (
      <>
        <path d="M7 3v18M17 3v18M7 7h10M7 11h10M7 15h10M7 19h10" {...p} />
      </>
    ),
    toolbox: (
      <>
        <rect x="3" y="8" width="18" height="12" rx="2" {...p} />
        <path d="M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 13h18M10 13v2h4v-2" {...p} />
      </>
    ),

    // --- Furniture ---
    sofa: (
      <>
        <path d="M3 12v-2a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2M3 12a2 2 0 0 1 2 2v3h14v-3a2 2 0 0 1 2-2M5 17v3M19 17v3M6 8v4M18 8v4" {...p} />
      </>
    ),
    bed: (
      <>
        <path d="M3 19V8M3 13h18v6M21 19v-5a3 3 0 0 0-3-3H9v2M3 19h18" {...p} />
        <circle cx="6.5" cy="9.5" r="2" {...p} />
      </>
    ),
    chair: (
      <>
        <path d="M7 3v9M17 3v9M6 12h12M7 12l-1 9M17 12l1 9M7 8h10" {...p} />
      </>
    ),
    table: (
      <>
        <path d="M3 9h18M5 9v11M19 9v11M9 9v4M15 9v4" {...p} />
      </>
    ),
    shelf: (
      <>
        <rect x="4" y="3" width="16" height="18" rx="1" {...p} />
        <path d="M4 9h16M4 15h16M8 4.5v3M14 10.5v3" {...p} />
      </>
    ),
    wardrobe: (
      <>
        <rect x="4" y="3" width="16" height="18" rx="1.5" {...p} />
        <path d="M12 3v18M9 11v2M15 11v2M6 21v1M18 21v1" {...p} />
      </>
    ),
    desk: (
      <>
        <path d="M3 8h18M4 8v12M20 8v12M4 14h6M4 20v-6h6v6" {...p} />
      </>
    ),
    mirror: (
      <>
        <rect x="7" y="3" width="10" height="16" rx="5" {...p} />
        <path d="M10 7c-1 1-1 3 0 4M9 21h6" {...p} />
      </>
    ),
    lamp: (
      <>
        <path d="M8 3h8l3 8H5zM12 11v8M8 19h8" {...p} />
      </>
    ),

    // --- Transport ---
    car: (
      <>
        <path d="M3 16v-3l2-5a2 2 0 0 1 2-1.3h10A2 2 0 0 1 19 8l2 5v3M3 16h18M3 16v2h2v-2M19 16v2h2v-2M5 12h14" {...p} />
        <circle cx="7.5" cy="16" r="1.6" {...p} />
        <circle cx="16.5" cy="16" r="1.6" {...p} />
      </>
    ),
    bike: (
      <>
        <circle cx="6" cy="16" r="4" {...p} />
        <circle cx="18" cy="16" r="4" {...p} />
        <path d="M6 16 10 8h6l-4 8M10 8H8M14 8l4 8M12 6h3" {...p} />
      </>
    ),
    scooter: (
      <>
        <circle cx="5" cy="18" r="3" {...p} />
        <circle cx="19" cy="18" r="3" {...p} />
        <path d="M8 18h8l-2-12h-3M14 6h3M14 18l4-9" {...p} />
      </>
    ),
    wheel: (
      <>
        <circle cx="12" cy="12" r="9" {...p} />
        <circle cx="12" cy="12" r="3.5" {...p} />
        <path d="M12 3v5.5M12 15.5V21M3 12h5.5M15.5 12H21" {...p} />
      </>
    ),

    // --- Property types ---
    apartment: (
      <>
        <rect x="4" y="3" width="16" height="18" rx="1.5" {...p} />
        <path d="M8 7h.01M12 7h.01M16 7h.01M8 11h.01M12 11h.01M16 11h.01M8 15h.01M16 15h.01M10 21v-4h4v4" {...p} />
      </>
    ),
    dacha: (
      <>
        <path d="M4 12 12 5l8 7M6 12v9h12v-9M10 21v-5h4v5" {...p} />
        <path d="M16 7V4h2v5" {...p} />
      </>
    ),
    garageBuilding: (
      <>
        <path d="M3 21V8l9-4 9 4v13M3 21h18M7 21V11h10v10M7 14h10M7 17h10" {...p} />
      </>
    ),

    // --- Misc domain ---
    book: (
      <>
        <path d="M5 4a1 1 0 0 1 1-1h13v15H6a1 1 0 0 0-1 1zM5 4v15a2 2 0 0 0 2 2h12M9 7h6" {...p} />
      </>
    ),
    clothes: (
      <>
        <path d="M9 3 4 6l2 4 2-1v9h8v-9l2 1 2-4-5-3a3 3 0 0 1-6 0z" {...p} />
      </>
    ),
    shoes: (
      <>
        <path d="M3 16v-7l3-1 2 3 6 1c3 0 7 1 7 4v1H3zM3 16h18M8 12l1 2" {...p} />
      </>
    ),
    plant: (
      <>
        <path d="M8 21h8l-1-7H9zM12 14V8M12 8c0-2 1.5-4 4-4 0 2.5-1.5 4-4 4zM12 11c0-2-1.5-3.5-4-3.5 0 2 1.5 3.5 4 3.5z" {...p} />
      </>
    ),
    document: (
      <>
        <path d="M6 3h8l5 5v13H6zM14 3v5h5M9 13h6M9 16h6M9 10h2" {...p} />
      </>
    ),
    key: (
      <>
        <circle cx="8" cy="8" r="4.5" {...p} />
        <path d="M11 11 20 20M17 17l2-2M14 14l2-2" {...p} />
      </>
    ),
    laptop: (
      <>
        <rect x="5" y="4" width="14" height="10" rx="1.5" {...p} />
        <path d="M3 18h18l-1.5-4H4.5z" {...p} />
      </>
    ),
    phone: (
      <>
        <rect x="6" y="2" width="12" height="20" rx="3" {...p} />
        <path d="M10 5h4M11 18.5h2" {...p} />
      </>
    ),
    clock: (
      <>
        <circle cx="12" cy="12" r="9" {...p} />
        <path d="M12 7v5l3.5 2.5" {...p} />
      </>
    ),
    bag: (
      <>
        <path d="M5 8h14l-1 13H6zM8 8V6a4 4 0 0 1 8 0v2" {...p} />
      </>
    ),
    guitar: (
      <>
        <path d="M14 3 21 10M16 5l-2 2M11 8l5 5M11 8a4 4 0 1 0-3 6 4 4 0 0 0 5 1 4 4 0 0 0-2-7z" {...p} />
        <circle cx="9" cy="14" r="1.6" {...p} />
      </>
    ),
    grill: (
      <>
        <path d="M4 5h16l-2 7H6zM6 12l-2 9M18 12l2 9M9 21h6M8 8h.01M12 8h.01M16 8h.01" {...p} />
      </>
    ),
    medkit: (
      <>
        <rect x="3" y="7" width="18" height="13" rx="2" {...p} />
        <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M12 11v5M9.5 13.5h5" {...p} />
      </>
    ),
  };
}

// Static list of icon names (used both at runtime for the demo grid and to
// derive the IconName type below).
export const ICON_NAMES = [
  'mic',
  'keyboard',
  'camera',
  'home',
  'tree',
  'cal',
  'bell',
  'search',
  'plus',
  'close',
  'check',
  'chevR',
  'chevD',
  'chevL',
  'back',
  'more',
  'undo',
  'move',
  'trash',
  'edit',
  'boiler',
  'filter',
  'drop',
  'spark',
  'photo',
  'box',
  'pkg',
  'map',
  'play',
  'flame',
  'bolt',
  'shield',
  'sun',
  'snow',
  'user',
  'waveform',
  'list',
  'pin',
  'leaf',
  'stop',
  'send',
  'sliders',
  'info',
  'history',
  'folder',
  'item',
  'kitchen',
  'bedroom',
  'bathroom',
  'livingRoom',
  'hallway',
  'pantry',
  'garageRoom',
  'balcony',
  'kidsRoom',
  'study',
  'attic',
  'basement',
  'closet',
  'laundry',
  'garden',
  'toilet',
  'fridge',
  'washer',
  'stove',
  'oven',
  'microwave',
  'dishwasher',
  'tv',
  'ac',
  'vacuum',
  'kettle',
  'hood',
  'radiator',
  'fan',
  'drill',
  'hammer',
  'screwdriver',
  'wrench',
  'saw',
  'ladder',
  'toolbox',
  'sofa',
  'bed',
  'chair',
  'table',
  'shelf',
  'wardrobe',
  'desk',
  'mirror',
  'lamp',
  'car',
  'bike',
  'scooter',
  'wheel',
  'apartment',
  'dacha',
  'garageBuilding',
  'book',
  'clothes',
  'shoes',
  'plant',
  'document',
  'key',
  'laptop',
  'phone',
  'clock',
  'bag',
  'guitar',
  'grill',
  'medkit',
] as const;

export type IconName = (typeof ICON_NAMES)[number];

export type IconProps = {
  name: IconName;
  size?: number;
  color?: string;
  strokeWidth?: number;
  style?: JSX.CSSProperties;
};

export function Icon({
  name,
  size = 20,
  color = 'currentColor',
  strokeWidth = 1.7,
  style,
}: IconProps): JSX.Element {
  const paths = buildPaths(color, strokeWidth);
  const child = paths[name] ?? null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={style}>
      {child}
    </svg>
  );
}
